import type { SessionWearable } from "@training/shared";
import { format } from "../lib/session.ts";
import { formatSleep, kcal, mainWorkout, recoveryTone } from "../lib/wearable.ts";

const TONE = { high: "text-gain", mid: "text-ink", low: "text-iron" } as const;

/** Zones 0 to 5, cool to hot: how the session spent its heart rate. */
const ZONE_COLOURS = ["bg-line", "bg-ink/25", "bg-ink/45", "bg-gain/70", "bg-hue/80", "bg-iron"];

/**
 * What the wearable saw of the session: the workout recorded while it ran,
 * and the night that led into it. Read after the session, beside the sets.
 */
export function SessionWearableCard({ wearable }: { wearable: SessionWearable | undefined }) {
  const day = wearable?.day ?? null;
  const workout = wearable ? mainWorkout(wearable.workouts) : null;
  if (!day && !workout) return null;

  const zones = workout?.zoneMinutes ?? null;
  const zoneTotal = zones?.reduce((sum, minutes) => sum + minutes, 0) ?? 0;

  return (
    <section className="card px-5 py-4" aria-label="Bracelet">
      {workout && (
        <>
          <p className="label">Pendant la séance</p>
          <dl className="num mt-2 grid grid-cols-4 gap-2">
            <Figure label="Strain" value={workout.strain === null ? "—" : format(workout.strain)} />
            <Figure label="FC moy." value={workout.averageHr === null ? "—" : String(workout.averageHr)} />
            <Figure label="FC max" value={workout.maxHr === null ? "—" : String(workout.maxHr)} />
            <Figure label="kcal" value={workout.kilojoule === null ? "—" : String(kcal(workout.kilojoule))} />
          </dl>
          {zones && zoneTotal > 0 && (
            <div className="mt-3">
              <div className="flex h-2 overflow-hidden rounded-full" role="img" aria-label="Temps par zone cardiaque">
                {zones.map((minutes, zone) =>
                  minutes > 0 ? (
                    <span
                      key={zone}
                      className={ZONE_COLOURS[zone]}
                      style={{ width: `${(minutes / zoneTotal) * 100}%` }}
                    />
                  ) : null,
                )}
              </div>
              <p className="num mt-1 text-[0.74rem] text-ink-mute">
                {zones
                  .map((minutes, zone) => (minutes > 0 ? `Z${zone} ${minutes}'` : null))
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          )}
        </>
      )}
      {day && (
        <div className={workout ? "mt-4 border-t border-line/60 pt-3" : ""}>
          <p className="label">La nuit d'avant</p>
          <dl className="num mt-2 grid grid-cols-3 gap-2">
            <Figure
              label="Récup"
              value={day.recoveryPct === null ? "—" : `${Math.round(day.recoveryPct)} %`}
              className={day.recoveryPct === null ? undefined : TONE[recoveryTone(day.recoveryPct)]}
            />
            <Figure label="HRV" value={day.hrvMs === null ? "—" : `${Math.round(day.hrvMs)} ms`} />
            <Figure label="Sommeil" value={day.sleepMinutes === null ? "—" : formatSleep(day.sleepMinutes)} />
          </dl>
        </div>
      )}
    </section>
  );
}

function Figure({ label, value, className = "text-ink" }: { label: string; value: string; className?: string }) {
  return (
    <div className="min-w-0">
      <dt className="label text-[0.62rem]">{label}</dt>
      <dd className={["mt-0.5 truncate text-[1.05rem] font-bold", className].join(" ")}>{value}</dd>
    </div>
  );
}
